import { createContext, useContext, useMemo, useState } from "react";

/** One shared Edit ⇄ Preview state for the whole admin surface. The role page and the candidate drawer both
 *  read/write it, so the two can never disagree (one showing Edit while the other shows Preview reads as a
 *  bug). Admins LAND in Preview — the default view is exactly what the client sees; editing is an opt-in the
 *  admin turns on, and once on it stays on as they move between the page and the drawer. Viewers never see the
 *  toggle (editing is always false for them), so the default is harmless there. */
interface EditPreview {
  previewClient: boolean;
  setPreviewClient: (v: boolean) => void;
  toggle: () => void;
}

const EditPreviewContext = createContext<EditPreview | null>(null);

export function EditPreviewProvider({ children }: { children: React.ReactNode }) {
  const [previewClient, setPreviewClient] = useState(true);
  const value = useMemo(
    () => ({ previewClient, setPreviewClient, toggle: () => setPreviewClient((p) => !p) }),
    [previewClient],
  );
  return <EditPreviewContext.Provider value={value}>{children}</EditPreviewContext.Provider>;
}

/** Shared Edit ⇄ Preview state. Falls back to a fixed Preview (no-op setters) outside a provider, so a drawer
 *  opened on a surface that doesn't wrap the provider degrades to the client view rather than crashing. */
export function useEditPreview(): EditPreview {
  const ctx = useContext(EditPreviewContext);
  if (ctx) return ctx;
  return { previewClient: true, setPreviewClient: () => {}, toggle: () => {} };
}
